import React, { useState, useEffect } from 'react';
import { 
    Container, Typography, Box, Paper, Grid, Button, TextField, 
    Dialog, DialogTitle, DialogContent, DialogActions, Alert,
    Table, TableBody, TableCell, TableHead, TableRow, IconButton,
    Select, MenuItem, FormControl, InputLabel, Chip,
    Card, CardContent, useTheme, Avatar, Divider, CardActionArea,
    Switch, FormControlLabel, CircularProgress
} from '@mui/material';
import { 
    Add, Delete, Edit, ArrowBack, Save, Business, Group, Extension, 
    Settings as SettingsIcon, CheckCircle, ColorLens 
} from '@mui/icons-material';
import api from '../../api/axios';
import { useNavigate, useLocation } from 'react-router-dom';

// --- Sub-Components for each section ---

const JobTypesSettings = () => {
    const [jobTypes, setJobTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [formData, setFormData] = useState({ 
        name: '', 
        default_duration_mins: 60, 
        color: '#3B82F6', 
        is_active: true 
    });
    const [message, setMessage] = useState(null);

    useEffect(() => {
        fetchJobTypes();
    }, []);

    const fetchJobTypes = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/job-types');
            setJobTypes(res.data || []);
        } catch (error) {
            console.error('Failed to fetch job types', error);
        }
        setLoading(false);
    };

    const handleAdd = () => {
        setEditingType(null);
        setFormData({ name: '', default_duration_mins: 60, color: '#3B82F6', is_active: true });
        setDialogOpen(true);
    };

    const handleEdit = (type) => {
        setEditingType(type);
        setFormData({
            name: type.name,
            default_duration_mins: type.default_duration_mins,
            color: type.color,
            is_active: type.is_active
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editingType) {
                await api.put(`/admin/job-types/${editingType.id}`, formData);
                setMessage({ type: 'success', text: 'Job type updated!' });
            } else {
                await api.post('/admin/job-types', formData);
                setMessage({ type: 'success', text: 'Job type added!' });
            }
            setDialogOpen(false);
            fetchJobTypes();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to save job type.' });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this job type?')) return;
        try {
            await api.delete(`/admin/job-types/${id}`);
            setMessage({ type: 'success', text: 'Job type removed.' });
            fetchJobTypes();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to remove job type.' });
        }
    };

    return (
        <Box>
            {message && <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={700}>Job Types</Typography>
                <Button 
                    variant="contained" 
                    startIcon={<Add />} 
                    onClick={handleAdd}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                    Add Job Type
                </Button>
            </Box>

            <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 3, border: '1px solid #E2E8F0' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Duration</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Color</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {jobTypes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                    No job types defined.
                                </TableCell>
                            </TableRow>
                        ) : (
                            jobTypes.map((type) => (
                                <TableRow key={type.id} hover>
                                    <TableCell sx={{ fontWeight: 500 }}>{type.name}</TableCell>
                                    <TableCell>{type.default_duration_mins} mins</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 16, height: 16, borderRadius: 4, bgcolor: type.color }} />
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{type.color}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={type.is_active ? 'Active' : 'Inactive'} 
                                            size="small" 
                                            color={type.is_active ? 'success' : 'default'}
                                            variant="outlined"
                                            sx={{ height: 24, fontWeight: 600 }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleEdit(type)} color="primary" sx={{ bgcolor: 'primary.50', mr: 1 }}>
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(type.id)} color="error" sx={{ bgcolor: 'error.50' }}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Paper>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 700 }}>{editingType ? 'Edit Job Type' : 'Add Job Type'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                            autoFocus
                            label="Name"
                            fullWidth
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                        <TextField
                            label="Default Duration (minutes)"
                            type="number"
                            fullWidth
                            value={formData.default_duration_mins}
                            onChange={(e) => setFormData({ ...formData, default_duration_mins: parseInt(e.target.value) || 0 })}
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Color Code</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1', '#14B8A6'].map(color => (
                                    <Box 
                                        key={color}
                                        onClick={() => setFormData({ ...formData, color })}
                                        sx={{ 
                                            width: 32, 
                                            height: 32, 
                                            borderRadius: '50%', 
                                            bgcolor: color, 
                                            cursor: 'pointer',
                                            border: formData.color === color ? '3px solid white' : 'none',
                                            boxShadow: formData.color === color ? `0 0 0 2px ${color}` : 'none',
                                            transition: 'all 0.2s'
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={formData.is_active} 
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} 
                                />
                            }
                            label="Active"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={!formData.name} sx={{ fontWeight: 600 }}>
                        {editingType ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const UsersSettings = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [userForm, setUserForm] = useState({ full_name: '', email: '', role: 'worker', password: '' });
    const [message, setMessage] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/users');
            setUsers(response.data || []);
        } catch (error) {
            console.error('Failed to fetch users', error);
        }
        setLoading(false);
    };

    const handleAdd = () => {
        setEditingUser(null);
        setUserForm({ full_name: '', email: '', role: 'worker', password: '' });
        setDialogOpen(true);
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setUserForm({ 
            full_name: user.full_name || '', 
            email: user.email || '', 
            role: user.role || 'worker',
            password: '' // Don't fill password
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editingUser) {
                // If password is empty, remove it from payload
                const payload = { ...userForm };
                if (!payload.password) delete payload.password;
                
                await api.put(`/admin/users/${editingUser.id}`, payload);
                setMessage({ type: 'success', text: 'User updated!' });
            } else {
                await api.post('/admin/users', userForm);
                setMessage({ type: 'success', text: 'User invited/created!' });
            }
            setDialogOpen(false);
            fetchUsers();
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save user.' });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to deactivate this user?')) return;
        try {
            await api.delete(`/admin/users/${id}`);
            setMessage({ type: 'success', text: 'User deactivated.' });
            fetchUsers();
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to deactivate user.' });
        }
    };

    const getRoleColor = (role) => {
        switch(role) {
            case 'admin': return 'error';
            case 'manager': return 'primary';
            default: return 'default';
        }
    };

    return (
        <Box>
            {message && <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={700}>Users & Roles</Typography>
                <Button 
                    variant="contained" 
                    startIcon={<Add />} 
                    onClick={handleAdd}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                    Invite User
                </Button>
            </Box>

            <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 3, border: '1px solid #E2E8F0' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Role</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Email</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                        No users found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                users.map((user) => (
                                    <TableRow key={user.id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.light', fontSize: '0.9rem', fontWeight: 600 }}>
                                                    {user.full_name?.[0]?.toUpperCase()}
                                                </Avatar>
                                                <Typography variant="body2" fontWeight={600}>{user.full_name}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={user.role || 'worker'} 
                                                size="small" 
                                                color={getRoleColor(user.role)}
                                                variant="filled"
                                                sx={{ fontWeight: 600, textTransform: 'capitalize', borderRadius: 1, height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>{user.email}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => handleEdit(user)} color="primary" sx={{ bgcolor: 'primary.50', mr: 1 }}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(user.id)} color="error" sx={{ bgcolor: 'error.50' }}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 700 }}>{editingUser ? 'Edit User' : 'Invite New User'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                            autoFocus
                            label="Full Name"
                            fullWidth
                            required
                            value={userForm.full_name}
                            onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            required
                            value={userForm.email}
                            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={userForm.role}
                                label="Role"
                                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="worker">Worker</MenuItem>
                                <MenuItem value="manager">Manager</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label={editingUser ? "New Password (optional)" : "Password"}
                            type="password"
                            fullWidth
                            required={!editingUser}
                            value={userForm.password}
                            onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: 2 } }}
                            helperText={editingUser ? "Leave blank to keep current password" : ""}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={!userForm.email || !userForm.full_name} sx={{ fontWeight: 600 }}>
                        {editingUser ? 'Update' : 'Invite'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const TeamSettings = () => {
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [memberForm, setMemberForm] = useState({ name: '', email: '', phone: '', role: 'worker' });
    const [message, setMessage] = useState(null);

    useEffect(() => {
        fetchTeam();
    }, []);

    const fetchTeam = async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/employees');
            setTeam(response.data || []);
        } catch (error) {
            console.error('Failed to fetch team', error);
        }
        setLoading(false);
    };

    const handleAdd = () => {
        setEditingMember(null);
        setMemberForm({ name: '', email: '', phone: '', role: 'worker' });
        setDialogOpen(true);
    };

    const handleEdit = (member) => {
        setEditingMember(member);
        setMemberForm({ 
            name: member.name || '', 
            email: member.email || '', 
            phone: member.phone || '', 
            role: member.role || 'worker' 
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editingMember) {
                await api.put(`/admin/employees/${editingMember.id}`, memberForm);
                setMessage({ type: 'success', text: 'Team member updated!' });
            } else {
                await api.post('/admin/employees', memberForm);
                setMessage({ type: 'success', text: 'Team member added!' });
            }
            setDialogOpen(false);
            fetchTeam();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to save team member.' });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to remove this team member?')) return;
        try {
            await api.delete(`/admin/employees/${id}`);
            setMessage({ type: 'success', text: 'Team member removed.' });
            fetchTeam();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to remove team member.' });
        }
    };

    const getRoleColor = (role) => {
        switch(role) {
            case 'manager': return 'primary';
            case 'admin': return 'error';
            default: return 'default';
        }
    };

    return (
        <Box>
            {message && <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={700}>Team Members</Typography>
                <Button 
                    variant="contained" 
                    startIcon={<Add />} 
                    onClick={handleAdd}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                    Add Member
                </Button>
            </Box>

            <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 3, border: '1px solid #E2E8F0' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Role</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Contact</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {team.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                        No team members found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                team.map((member) => (
                                    <TableRow key={member.id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.light', fontSize: '0.9rem', fontWeight: 600 }}>
                                                    {member.name?.[0]?.toUpperCase()}
                                                </Avatar>
                                                <Typography variant="body2" fontWeight={600}>{member.name}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={member.role || 'worker'} 
                                                size="small" 
                                                color={getRoleColor(member.role)}
                                                variant="filled"
                                                sx={{ fontWeight: 600, textTransform: 'capitalize', borderRadius: 1, height: 24 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>{member.email}</Typography>
                                            <Typography variant="caption" color="text.secondary">{member.phone}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => handleEdit(member)} color="primary" sx={{ bgcolor: 'primary.50', mr: 1, '&:hover': { bgcolor: 'primary.100' } }}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(member.id)} color="error" sx={{ bgcolor: 'error.50', '&:hover': { bgcolor: 'error.100' } }}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 700 }}>{editingMember ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                            autoFocus
                            label="Name"
                            fullWidth
                            required
                            value={memberForm.name}
                            onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            value={memberForm.email}
                            onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                        <TextField
                            label="Phone"
                            fullWidth
                            value={memberForm.phone}
                            onChange={(e) => setMemberForm({ ...memberForm, phone: e.target.value })}
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={memberForm.role}
                                label="Role"
                                onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="worker">Worker</MenuItem>
                                <MenuItem value="manager">Manager</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={!memberForm.name} sx={{ fontWeight: 600 }}>
                        {editingMember ? 'Update' : 'Add Member'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const IntegrationSettings = () => {
    const [integrations, setIntegrations] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        fetchIntegrations();
    }, []);

    const fetchIntegrations = async () => {
        try {
            const response = await api.get('/integrations/');
            setIntegrations(response.data || []);
        } catch (error) {
            console.error('Failed to fetch integrations', error);
        }
    };

    const handleConnect = (provider) => {
        setSelectedProvider(provider);
        setApiKey('');
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            await api.post(`/integrations/${selectedProvider}`, {
                api_key: apiKey,
                settings: { enabled: true }
            });
            setMessage({ type: 'success', text: 'Integration connected successfully!' });
            setDialogOpen(false);
            fetchIntegrations();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to connect integration.' });
        }
    };

    const isConnected = (provider) => {
        return integrations.some(i => i.provider === provider && i.is_active);
    };

    return (
        <Box>
            {message && <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
            
            <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>Integrations</Typography>
            
            <Grid container spacing={3}>
                {['xero', 'fastfields', 'gmail', 'slack'].map((provider) => (
                    <Grid item xs={12} md={6} key={provider}>
                        <Card 
                            variant="outlined" 
                            sx={{ 
                                borderRadius: 3, 
                                border: '1px solid',
                                borderColor: isConnected(provider) ? 'success.main' : '#E2E8F0',
                                height: '100%',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Avatar variant="rounded" sx={{ bgcolor: isConnected(provider) ? 'success.main' : 'action.disabledBackground', width: 48, height: 48 }}>
                                            <Extension />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6" sx={{ textTransform: 'capitalize', fontWeight: 700, fontSize: '1rem' }}>{provider}</Typography>
                                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                                {isConnected(provider) ? 'Connected & Syncing' : 'Not connected'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {isConnected(provider) && <CheckCircle color="success" />}
                                </Box>
                                <Button 
                                    variant={isConnected(provider) ? "outlined" : "contained"}
                                    onClick={() => handleConnect(provider)}
                                    fullWidth
                                    color={isConnected(provider) ? "success" : "primary"}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1 }}
                                >
                                    {isConnected(provider) ? 'Configure Settings' : 'Connect Integration'}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ textTransform: 'capitalize', fontWeight: 700 }}>Connect {selectedProvider}</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                        You are about to connect your {selectedProvider} account. Please provide your API credentials below.
                    </Alert>
                    <TextField
                        autoFocus
                        label="API Key / Access Token"
                        fullWidth
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        variant="outlined"
                        type="password"
                        placeholder="Enter your secret key"
                        InputProps={{ sx: { borderRadius: 2 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ fontWeight: 600 }}>Save Connection</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const GeneralPlaceholder = ({ title, description }) => (
    <Box>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {description}
        </Typography>
        <Paper variant="outlined" sx={{ p: 8, textAlign: 'center', borderRadius: 3, bgcolor: '#F8FAFC', border: '1px dashed #E2E8F0' }}>
            <SettingsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.3 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Configuration Required</Typography>
            <Typography variant="body2" color="text.secondary">
                Configuration options for {title} will appear here once implemented.
            </Typography>
        </Paper>
    </Box>
);

const BusinessProfile = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '', // Note: Organization model doesn't have phone/email directly on it in entities.py, assuming it might be added or we use address/service_areas. 
                   // Wait, checking entities.py: Organization has name, address, service_areas, subscription_plan, booking_mode. 
                   // It does NOT have email/phone. I should probably add them or just map what exists. 
                   // For now, I'll stick to what is in the Organization model: name, address, booking_mode, service_areas.
        address: '',
        booking_mode: 'we_schedule',
        service_areas: ''
    });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        fetchOrgSettings();
    }, []);

    const fetchOrgSettings = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/organization');
            if (res.data) {
                setFormData({
                    name: res.data.name || '',
                    address: res.data.address || '',
                    booking_mode: res.data.booking_mode || 'we_schedule',
                    service_areas: res.data.service_areas || ''
                });
            }
        } catch (error) {
            console.error('Failed to fetch organization settings', error);
        }
        setLoading(false);
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSave = async () => {
        try {
            await api.put('/admin/organization', formData);
            setMessage({ type: 'success', text: 'Business profile updated!' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to save business profile.' });
        }
    };

    return (
        <Box>
            {message && <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
            
            <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>Business Profile</Typography>
            <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, border: '1px solid #E2E8F0' }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField 
                            fullWidth 
                            label="Business Name" 
                            name="name"
                            value={formData.name} 
                            onChange={handleChange}
                            variant="outlined" 
                            InputProps={{ sx: { borderRadius: 2 } }} 
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField 
                            fullWidth 
                            label="Address / Base Location" 
                            name="address"
                            multiline 
                            rows={2} 
                            value={formData.address} 
                            onChange={handleChange}
                            variant="outlined" 
                            InputProps={{ sx: { borderRadius: 2 } }} 
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Booking Mode</InputLabel>
                            <Select
                                name="booking_mode"
                                value={formData.booking_mode}
                                label="Booking Mode"
                                onChange={handleChange}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="we_schedule">We Schedule (AI/Manager Optimized)</MenuItem>
                                <MenuItem value="customer_chooses">Customer Chooses (Calendar Slots)</MenuItem>
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            "We Schedule": Intake captures preferences, AI suggests optimal times. "Customer Chooses": Customer picks specific slot.
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField 
                            fullWidth 
                            label="Service Areas (Postcodes/Suburbs)" 
                            name="service_areas"
                            value={formData.service_areas} 
                            onChange={handleChange}
                            placeholder="e.g. 4000, 4001, Brisbane"
                            variant="outlined" 
                            InputProps={{ sx: { borderRadius: 2 } }} 
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button 
                                variant="contained" 
                                startIcon={<Save />} 
                                size="large" 
                                onClick={handleSave}
                                disabled={loading}
                                sx={{ borderRadius: 2, fontWeight: 600, px: 4 }}
                            >
                                {loading ? 'Loading...' : 'Save Changes'}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

// --- Main Settings Router Component ---

const NotificationSettings = () => {
    const [formData, setFormData] = useState({
        job_failed: true,
        client_escalation: true,
        worker_needs_help: true,
        sick_leave_request: true,
        unconfirmed_reminder: true,
        integration_failure: true
    });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/organization');
            if (res.data && res.data.config) {
                const config = JSON.parse(res.data.config);
                if (config.notifications) {
                    setFormData({
                        job_failed: config.notifications.job_failed ?? true,
                        client_escalation: config.notifications.client_escalation ?? true,
                        worker_needs_help: config.notifications.worker_needs_help ?? true,
                        sick_leave_request: config.notifications.sick_leave_request ?? true,
                        unconfirmed_reminder: config.notifications.unconfirmed_reminder ?? true,
                        integration_failure: config.notifications.integration_failure ?? true
                    });
                }
            }
        } catch (error) {
            console.error('Failed to fetch notification settings', error);
        }
        setLoading(false);
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.checked
        });
    };

    const handleSave = async () => {
        try {
            const res = await api.get('/admin/organization');
            const currentConfig = res.data.config ? JSON.parse(res.data.config) : {};
            
            const configPayload = {
                ...currentConfig,
                notifications: formData
            };

            await api.put('/admin/organization', {
                config: JSON.stringify(configPayload)
            });
            setMessage({ type: 'success', text: 'Notification settings updated!' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to save notification settings.' });
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

    const notificationItems = [
        { id: 'job_failed', label: 'Job Failed', desc: 'Alert when a worker marks a job as failed on site.' },
        { id: 'client_escalation', label: 'Client Escalation', desc: 'Alert when the AI cannot resolve a client dispute or question.' },
        { id: 'worker_needs_help', label: 'Worker Needs Help', desc: 'Alert when a worker flags a safety concern or assistance request.' },
        { id: 'sick_leave_request', label: 'Sick Leave Request', desc: 'Alert when a team member submits an urgent leave request.' },
        { id: 'unconfirmed_reminder', label: 'Unconfirmed Jobs Reminder', desc: 'Morning alert if jobs for today are still unconfirmed.' },
        { id: 'integration_failure', label: 'Integration Failure', desc: 'Alert if Xero, Google, or Twilio sync fails.' }
    ];

    return (
        <Box>
            {message && <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
            
            <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>Notification Preferences</Typography>
            <Paper variant="outlined" sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                    {notificationItems.map((item, index) => (
                        <React.Fragment key={item.id}>
                            <ListItem sx={{ py: 2, px: 3 }}>
                                <ListItemText 
                                    primary={<Typography fontWeight={600}>{item.label}</Typography>}
                                    secondary={item.desc}
                                />
                                <Switch 
                                    edge="end"
                                    name={item.id}
                                    checked={formData[item.id]}
                                    onChange={handleChange}
                                />
                            </ListItem>
                            {index < notificationItems.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}
                </List>
            </Paper>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button 
                    variant="contained" 
                    startIcon={<Save />} 
                    onClick={handleSave}
                    sx={{ borderRadius: 2, fontWeight: 600, px: 4 }}
                >
                    Save Preferences
                </Button>
            </Box>
        </Box>
    );
};

const CommunicationTemplatesSettings = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        channel: 'sms',
        subject: '',
        content: '',
        is_active: true
    });
    const [message, setMessage] = useState(null);

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/communication/templates');
            setTemplates(res.data || []);
        } catch (error) {
            console.error('Failed to fetch templates', error);
        }
        setLoading(false);
    };

    const handleAdd = () => {
        setEditingTemplate(null);
        setFormData({ name: '', channel: 'sms', subject: '', content: '', is_active: true });
        setDialogOpen(true);
    };

    const handleEdit = (template) => {
        setEditingTemplate(template);
        setFormData({
            name: template.name,
            channel: template.channel,
            subject: template.subject || '',
            content: template.content,
            is_active: template.is_active
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editingTemplate) {
                await api.put(`/admin/communication/templates/${editingTemplate.id}`, formData);
                setMessage({ type: 'success', text: 'Template updated!' });
            } else {
                await api.post('/admin/communication/templates', formData);
                setMessage({ type: 'success', text: 'Template created!' });
            }
            setDialogOpen(false);
            fetchTemplates();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to save template.' });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this template?')) return;
        try {
            await api.delete(`/admin/communication/templates/${id}`);
            setMessage({ type: 'success', text: 'Template deleted.' });
            fetchTemplates();
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to delete template.' });
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

    return (
        <Box>
            {message && <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={700}>Communication Templates</Typography>
                <Button 
                    variant="contained" 
                    startIcon={<Add />} 
                    onClick={handleAdd}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                    Add Template
                </Button>
            </Box>

            <Paper variant="outlined" sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Channel</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {templates.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    No templates found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            templates.map((template) => (
                                <TableRow key={template.id} hover>
                                    <TableCell sx={{ fontWeight: 500 }}>{template.name}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={template.channel.toUpperCase()} 
                                            size="small" 
                                            variant="outlined"
                                            sx={{ fontWeight: 600, borderRadius: 1 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={template.is_active ? 'Active' : 'Inactive'} 
                                            color={template.is_active ? 'success' : 'default'}
                                            size="small"
                                            sx={{ fontWeight: 600, borderRadius: 1 }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleEdit(template)} color="primary" sx={{ mr: 1 }}>
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(template.id)} color="error">
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Paper>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 700 }}>{editingTemplate ? 'Edit Template' : 'Add Template'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                            label="Template Name"
                            fullWidth
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g., Job Confirmation"
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Channel</InputLabel>
                            <Select
                                value={formData.channel}
                                label="Channel"
                                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="sms">SMS</MenuItem>
                                <MenuItem value="email">Email</MenuItem>
                            </Select>
                        </FormControl>
                        {formData.channel === 'email' && (
                            <TextField
                                label="Email Subject"
                                fullWidth
                                value={formData.subject}
                                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                variant="outlined"
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        )}
                        <TextField
                            label="Message Content"
                            fullWidth
                            required
                            multiline
                            rows={6}
                            value={formData.content}
                            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                            placeholder="Use {{client_name}}, {{address}}, etc. for variables"
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                        <FormControlLabel
                            control={
                                <Switch 
                                    checked={formData.is_active} 
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} 
                                />
                            }
                            label="Active"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
                    <Button 
                        onClick={handleSave} 
                        variant="contained" 
                        disabled={!formData.name || !formData.content}
                        sx={{ fontWeight: 600 }}
                    >
                        {editingTemplate ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const SchedulingSettings = () => {
    const [formData, setFormData] = useState({
        booking_mode: 'we_schedule',
        working_hours_start: '08:00',
        working_hours_end: '17:00',
        confirmation_timing_days: 1,
        auto_send_confirmations: true
    });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/organization');
            if (res.data) {
                const config = res.data.config ? JSON.parse(res.data.config) : {};
                setFormData({
                    booking_mode: res.data.booking_mode || 'we_schedule',
                    working_hours_start: config.working_hours?.start || '08:00',
                    working_hours_end: config.working_hours?.end || '17:00',
                    confirmation_timing_days: config.scheduling?.confirmation_timing_days || 1,
                    auto_send_confirmations: config.scheduling?.auto_send_confirmations ?? true
                });
            }
        } catch (error) {
            console.error('Failed to fetch scheduling settings', error);
        }
        setLoading(false);
    };

    const handleChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData({
            ...formData,
            [e.target.name]: value
        });
    };

    const handleSave = async () => {
        try {
            const configPayload = {
                working_hours: {
                    start: formData.working_hours_start,
                    end: formData.working_hours_end
                },
                scheduling: {
                    confirmation_timing_days: parseInt(formData.confirmation_timing_days),
                    auto_send_confirmations: formData.auto_send_confirmations
                }
            };

            await api.put('/admin/organization', {
                booking_mode: formData.booking_mode,
                config: JSON.stringify(configPayload)
            });
            setMessage({ type: 'success', text: 'Scheduling settings updated!' });
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to save scheduling settings.' });
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

    return (
        <Box>
            {message && <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
            
            <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>Scheduling Settings</Typography>
            <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, border: '1px solid #E2E8F0' }}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Booking Mode</InputLabel>
                            <Select
                                name="booking_mode"
                                value={formData.booking_mode}
                                label="Booking Mode"
                                onChange={handleChange}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="we_schedule">We Schedule (AI/Manager Optimized)</MenuItem>
                                <MenuItem value="customer_chooses">Customer Chooses (Calendar Slots)</MenuItem>
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Control whether customers pick their own time or just submit requests for you to optimize.
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Working Hours Start"
                            name="working_hours_start"
                            type="time"
                            value={formData.working_hours_start}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Working Hours End"
                            name="working_hours_end"
                            type="time"
                            value={formData.working_hours_end}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Confirmation Timing (Days Before)"
                            name="confirmation_timing_days"
                            type="number"
                            value={formData.confirmation_timing_days}
                            onChange={handleChange}
                            helperText="When to send the lock-in SMS"
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch 
                                    name="auto_send_confirmations"
                                    checked={formData.auto_send_confirmations} 
                                    onChange={handleChange} 
                                />
                            }
                            label="Auto-send Confirmation Messages"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button 
                                variant="contained" 
                                startIcon={<Save />} 
                                onClick={handleSave}
                                sx={{ borderRadius: 2, fontWeight: 600, px: 4 }}
                            >
                                Save Scheduling Settings
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

const Settings = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Determine current section from path
    // e.g. /settings/users -> 'users'
    const section = location.pathname.split('/').pop();
    const isRoot = location.pathname === '/settings';

    // Map sections to components and titles
    const sections = {
        'users': { title: 'Users & Roles', component: <UsersSettings /> },
        'integrations': { title: 'Integrations', component: <IntegrationSettings /> },
        'business': { title: 'Business Profile', component: <BusinessProfile /> },
        'job-types': { title: 'Job Types', component: <JobTypesSettings /> },
        'scheduling': { title: 'Scheduling', component: <SchedulingSettings /> },
        'templates': { title: 'Templates', component: <CommunicationTemplatesSettings /> },
        'notifications': { title: 'Notifications', component: <NotificationSettings /> },
        'billing': { title: 'Billing', component: <GeneralPlaceholder title="Billing" description="View invoices and manage subscription plan." /> },
    };

    const currentSection = sections[section];

    // If at root /settings, redirect to users or show list (optional)
    useEffect(() => {
        if (isRoot) {
            navigate('/settings/business', { replace: true });
        }
    }, [isRoot, navigate]);

    if (!currentSection) return null;

    return (
        <Container maxWidth="md" sx={{ mt: 2, pb: 10 }}>
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
                <IconButton 
                    onClick={() => navigate('/more')} 
                    sx={{ 
                        mr: 2, 
                        color: 'text.primary', 
                        border: '1px solid #E2E8F0', 
                        bgcolor: 'white',
                        '&:hover': { bgcolor: '#F8FAFC' }
                    }}
                >
                    <ArrowBack fontSize="small" />
                </IconButton>
                <Box>
                    <Typography variant="h5" fontWeight={800} color="text.primary">
                        {currentSection.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage your {currentSection.title.toLowerCase()} settings
                    </Typography>
                </Box>
            </Box>
            
            {currentSection.component}
        </Container>
    );
};

export default Settings;

